<template>
  <div class="itinerary-table">
    <el-collapse v-model="activeDays" accordion>
      <el-collapse-item
        v-for="(day, index) in itinerary"
        :key="index"
        :name="index"
        class="day-collapse-item"
      >
        <template #title>
          <div class="day-header">
            <span class="day-number">第{{ day.day }}天</span>
            <span class="day-activities-count">{{ day.activities.length }}个活动</span>
          </div>
        </template>

        <el-timeline>
          <el-timeline-item
            v-for="(activity, actIndex) in day.activities"
            :key="actIndex"
            :type="getActivityType(activity.type)"
            :icon="getActivityIcon(activity.type)"
            :timestamp="activity.time"
            placement="top"
          >
            <div class="activity-card">
              <div class="activity-type-badge" :class="activity.type">
                {{ getActivityLabel(activity.type) }}
              </div>
              <h4 v-if="activity.name" class="activity-name">{{ activity.name }}</h4>
              <p class="activity-desc">{{ activity.description }}</p>
              
              <div v-if="activity.duration" class="activity-meta">
                <el-icon><Timer /></el-icon>
                <span>{{ activity.duration }}</span>
              </div>

              <div v-if="activity.budget && activity.budget > 0" class="activity-meta budget">
                <el-icon><Money /></el-icon>
                <span>¥{{ activity.budget }}</span>
              </div>

              <div v-if="activity.highlights && activity.highlights.length > 0" class="highlights">
                <el-tag
                  v-for="(highlight, hIndex) in activity.highlights"
                  :key="hIndex"
                  size="small"
                  type="warning"
                  effect="light"
                  class="highlight-tag"
                >
                  ✨ {{ highlight }}
                </el-tag>
              </div>
            </div>
          </el-timeline-item>
        </el-timeline>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Timer, Money, Location, Food, Tickets, Van, House } from '@element-plus/icons-vue'

const props = defineProps({
  itinerary: {
    type: Array,
    required: true
  }
})

const activeDays = ref([0])

const getActivityType = (type) => {
  const types = {
    'drive': 'primary',
    'attraction': 'success',
    'meal': 'warning',
    'hotel': 'info'
  }
  return types[type] || 'info'
}

const getActivityIcon = (type) => {
  const icons = {
    'drive': Van,
    'attraction': Tickets,
    'meal': Food,
    'hotel': House
  }
  return icons[type] || Location
}

const getActivityLabel = (type) => {
  const labels = {
    'drive': '驾车',
    'attraction': '景点',
    'meal': '餐饮',
    'hotel': '住宿'
  }
  return labels[type] || '活动'
}
</script>

<style scoped>
.itinerary-table {
  padding: 10px 0;
}

.day-collapse-item :deep(.el-collapse-item__header) {
  padding: 15px 20px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 10px;
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 600;
}

.day-header {
  display: flex;
  align-items: center;
  gap: 15px;
}

.day-number {
  background: linear-gradient(135deg, #00B5FF 0%, #0077FF 100%);
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
}

.day-activities-count {
  color: #666;
  font-size: 13px;
  font-weight: normal;
}

.activity-card {
  background: white;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  border-left: 3px solid #00B5FF;
}

.activity-type-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 12px;
  margin-bottom: 10px;
  font-weight: 500;
}

.activity-type-badge.drive {
  background: #e3f2fd;
  color: #1976d2;
}

.activity-type-badge.attraction {
  background: #e8f5e9;
  color: #388e3c;
}

.activity-type-badge.meal {
  background: #fff3e0;
  color: #f57c00;
}

.activity-type-badge.hotel {
  background: #f3e5f5;
  color: #7b1fa2;
}

.activity-name {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #333;
}

.activity-desc {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}

.activity-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #999;
  font-size: 13px;
  margin-top: 8px;
}

.activity-meta.budget {
  color: #FF6B35;
  font-weight: 600;
}

.highlights {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.highlight-tag {
  margin: 0;
}

:deep(.el-timeline-item__node) {
  background: linear-gradient(135deg, #00B5FF 0%, #0077FF 100%);
}

:deep(.el-timeline-item__wrapper) {
  padding-left: 20px;
}

:deep(.el-timeline-item__timestamp) {
  color: #FF6B35;
  font-weight: 600;
}
</style>